import { isNumber, tryJsonParse } from '@xemplo/common-util';
import { AssignTo, PayrunSteps, StatusToStepMap } from '@xemplo/gp-types';
import { safeCall } from '@xemplo/safe-call';

/**
 * Returns the individual payrun status ids that make up a step. Used to build the
 * `statusIds` array filter on the payrun list endpoint.
 * @param step PayrunStep to expand
 * @returns the status ids that match the step
 */
export function getStepStatusIds(step: PayrunSteps): number[] {
  const statusIds = [...StatusToStepMap(true)[step]];
  if (step === PayrunSteps.Complete) {
    statusIds.push(18);
  }
  return statusIds;
}
/**
 * @returns reduces the input array of numbers to a single numbber that is used by the BE for filtering
 */
export function transformStatus(numbers: number[]): number {
  return numbers.reduce((accumulator: number, currentNumber: number) => {
    return accumulator + Math.pow(2, currentNumber - 1);
  }, 0);
}

/** This function parses the json string for assignTo value on amendments */
export function getAssignedWorker(assignTo?: string | null) {
  if (assignTo === 'All') return [{ firstName: '', lastName: '', id: '' }] as AssignTo[]; //Edge case for old data
  return tryJsonParse<AssignTo[]>(assignTo ?? '');
}

export function getAssignedWorkerDisplayName(assignTo?: string | null) {
  const parsedAssignTo = getAssignedWorker(assignTo);
  switch (parsedAssignTo?.length) {
    case 0:
    case null:
    case undefined:
      return 'No Workers attached';
    case 1:
      return `${parsedAssignTo[0].firstName} ${parsedAssignTo[0].lastName}`;
    default:
      return `${parsedAssignTo?.length} Workers`;
  }
}

export const getRequestedByUserName = (value?: string | null) => {
  if (!value) return 'N/A';
  try {
    return JSON.parse(value)?.UserName;
  } catch {
    return 'N/A';
  }
};

export function amendmentRateUnitFieldValidation(
  value: string | number | undefined,
  comparer: number | undefined,
  max: number | undefined,
  customMsg?: string,
) {
  if (!comparer || !max) return true;

  if (typeof value === 'string' && isNaN(Number(value)))
    return 'This field must be a number';

  return value && Number(value) * comparer > max
    ? (customMsg ?? 'The total amount (rate x units) exceeds the maximum amount')
    : true;
}

export function fieldMustBeNumber(value: string | number | undefined) {
  return isNumber(value) ? true : 'This field must be a number';
}

const CONTENT_DISPOSITION_FILENAME_REGEX = /filename\*?=["']?(?:UTF-8'')?([^"';]+)["']?/i;

/**
 * Extracts the filename from a `Content-Disposition` response header.
 *
 * When the server streams a file (e.g. a payrun report or document download), it
 * communicates the intended filename via the `Content-Disposition` header rather than
 * the URL path. Since the browser does not automatically surface this value when
 * handling blobs programmatically, we must parse it ourselves so we can pass a
 * meaningful filename to the save-as dialog.
 *
 * Both the plain `filename=` token and the RFC 5987 `filename*=UTF-8''` encoded form
 * are supported. If parsing fails for any reason, `fallbackFilename` is returned.
 *
 * @param contentDisposition - The raw `Content-Disposition` header value.
 * @param fallbackFilename - Filename to use when the header is absent or unparseable.
 * @returns The decoded filename string.
 */
export function extractFilenameFromContentDisposition(
  contentDisposition?: string,
  fallbackFilename = 'download',
): string {
  if (!contentDisposition) return fallbackFilename;

  const [result, error] = safeCall(() => {
    const match = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_REGEX);
    if (!match) return fallbackFilename;
    return decodeURIComponent(match[1]);
  });

  if (error || !result) return fallbackFilename;
  return result;
}
